import * as React from "react";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import clsx from "clsx";

import "./styles.scss";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";

interface IAccordionContent {
  id: string; // Identifier for the accordion
  title: JSX.Element;
  body: JSX.Element;
}

export type AccordionContent = IAccordionContent | null;

export interface IAccordionCollectionProps {
  className?: string;
  accordionContents: AccordionContent[];
  expandIcon?: React.ReactNode;
  defaultExpandedId?: string; // Expand by ID
}

export const AccordionCollection = (props: IAccordionCollectionProps) => {
  const { className, accordionContents, expandIcon, defaultExpandedId } = props;
  const [expanded, setExpanded] = React.useState<string | false>(
    defaultExpandedId || false
  );
  const accordionRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());

  React.useEffect(() => {
    if (defaultExpandedId) {
      setExpanded(defaultExpandedId);
      const accordionElement = accordionRefs.current.get(defaultExpandedId);
      if (accordionElement) {
        accordionElement.scrollIntoView({
          behavior: "auto",
          block: "start",
        });
      }
    }
  }, [defaultExpandedId]);

  const handleChange =
    (panel: string) =>
    (_: React.ChangeEvent<unknown>, isExpanded: boolean): void => {
      setExpanded(isExpanded ? panel : false);
    };

  const renderAccordions = () =>
    accordionContents.map((content, index) =>
      content !== null ? (
        <Accordion
          key={content.id}
          ref={(el) => {
            if (el) {
              accordionRefs.current.set(content.id, el as HTMLDivElement);
            } else {
              accordionRefs.current.delete(content.id);
            }
          }}
          className="accordion-collection-child-content"
          expanded={expanded === content.id}
          onChange={handleChange(content.id)}
        >
          <AccordionSummary
            className="accordion-title"
            expandIcon={
              expandIcon ?? (
                <FontAwesomeIcon
                  icon={faChevronDown as IconProp}
                  className="chevron-expand-icon"
                />
              )
            }
          >
            {content.title}
          </AccordionSummary>
          <AccordionDetails className="accordion-body">
            {content.body}
          </AccordionDetails>
        </Accordion>
      ) : null
    );

  return (
    <Box className={clsx(className, "accordion-collection-root")}>
      {renderAccordions()}
    </Box>
  );
};
